/**
 * CampaignNumberEditDialog
 *
 * Dedicated action ("Zmień numer kampanii") that overrides the already
 * system-generated campaign number — e.g. appending the department's
 * initials-and-month convention. Reuses the offer's existing edit-access
 * check (canEdit). Only shown once a number has been allocated; the
 * endpoint itself never allocates one — see offer-approval spec
 * "Campaign number is system-generated and manually editable".
 */
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ApiErrorCode, parseApiError, UpdateCampaignNumberSchema } from '@ooh/contracts';
import type { Offer, ParsedApiError } from '@ooh/contracts';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { DropdownMenuItem } from '@ooh/ui/components/dropdown-menu';
import { Input } from '@ooh/ui/components/input';
import { Label } from '@ooh/ui/components/label';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { toast } from 'sonner';
import { useUpdateCampaignNumber } from '../api';

// Derived from the server-side schema so a bound change never silently
// diverges between client validation and the API (UpdateCampaignNumberSchema.campaignNumber).
const CAMPAIGN_NUMBER_MIN_LENGTH = UpdateCampaignNumberSchema.shape.campaignNumber.minLength ?? 1;
const CAMPAIGN_NUMBER_MAX_LENGTH = UpdateCampaignNumberSchema.shape.campaignNumber.maxLength ?? 64;

interface CampaignNumberForm {
  campaignNumber: string;
}

type CampaignNumberErrorToastKey =
  | 'workspace.header.toasts.conflict'
  | 'workspace.header.toasts.campaignNumberTaken'
  | 'workspace.header.toasts.campaignNumberNotAllocated'
  | 'workspace.header.toasts.campaignNumberOfferTrashed'
  | 'workspace.header.toasts.campaignNumberForbidden'
  | 'workspace.header.toasts.campaignNumberError';

/** Maps a PATCH /offers/:id/campaign-number failure to its translated toast key. */
function getCampaignNumberErrorToastKey(parsedError: ParsedApiError): CampaignNumberErrorToastKey {
  const { errorCode, statusCode } = parsedError;

  if (statusCode === 409 && errorCode === ApiErrorCode.CONFLICT) {
    return 'workspace.header.toasts.conflict';
  }
  if (statusCode === 409 && errorCode === ApiErrorCode.CAMPAIGN_NUMBER_TAKEN) {
    return 'workspace.header.toasts.campaignNumberTaken';
  }
  if (statusCode === 422 && errorCode === ApiErrorCode.CAMPAIGN_NUMBER_NOT_ALLOCATED) {
    return 'workspace.header.toasts.campaignNumberNotAllocated';
  }
  if (statusCode === 409 && errorCode === ApiErrorCode.OFFER_TRASHED) {
    return 'workspace.header.toasts.campaignNumberOfferTrashed';
  }
  if (statusCode === 403) {
    return 'workspace.header.toasts.campaignNumberForbidden';
  }

  return 'workspace.header.toasts.campaignNumberError';
}

export function CampaignNumberMenuItem({
  campaignNumber,
  canEdit,
  onSelect,
}: {
  campaignNumber: string | null;
  canEdit: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation('offers');

  if (!canEdit || campaignNumber === null) {
    return null;
  }

  return (
    <DropdownMenuItem data-testid="edit-campaign-number-menu-item" onSelect={onSelect}>
      {t('workspace.header.menu.editCampaignNumber')}
    </DropdownMenuItem>
  );
}

export function CampaignNumberEditDialog({
  offer,
  onClose,
}: {
  offer: Offer;
  onClose: () => void;
}) {
  const { t } = useTranslation('offers');
  const updateCampaignNumber = useUpdateCampaignNumber();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<CampaignNumberForm>({
    defaultValues: { campaignNumber: offer.campaignNumber ?? '' },
    mode: 'onChange',
  });
  const campaignNumberValue = useWatch({
    control,
    defaultValue: offer.campaignNumber ?? '',
    name: 'campaignNumber',
  });

  function closeDialog() {
    reset({ campaignNumber: offer.campaignNumber ?? '' });
    onClose();
  }

  function validateCampaignNumber(value: string) {
    const trimmedValue = value.trim();

    if (trimmedValue.length < CAMPAIGN_NUMBER_MIN_LENGTH) {
      return t('workspace.header.campaignNumberDialog.errors.required');
    }

    if (trimmedValue.length > CAMPAIGN_NUMBER_MAX_LENGTH) {
      return t('workspace.header.campaignNumberDialog.errors.tooLong');
    }

    return true;
  }

  async function handleCampaignNumberSubmit(values: CampaignNumberForm) {
    try {
      await updateCampaignNumber.mutateAsync({
        id: String(offer.id),
        campaignNumber: values.campaignNumber.trim(),
        updatedAt: offer.updatedAt,
      });

      closeDialog();
      toast.success(t('workspace.header.toasts.campaignNumberUpdated'));
    } catch (error) {
      toast.error(t(getCampaignNumberErrorToastKey(parseApiError(error))));
    }
  }

  return (
    <ModalDialog
      closeLabel={t('workspace.header.campaignNumberDialog.close')}
      footer={
        <>
          <Button type="button" variant="outline" onClick={closeDialog}>
            {t('workspace.header.campaignNumberDialog.cancel')}
          </Button>
          <Button
            type="submit"
            // eslint-disable-next-line i18next/no-literal-string -- form id, not user-visible
            form="campaign-number-form"
            disabled={!isValid || updateCampaignNumber.isPending}
          >
            {t('workspace.header.campaignNumberDialog.confirm')}
          </Button>
        </>
      }
      header={
        <div>
          <h2 className="text-base font-semibold">
            {t('workspace.header.campaignNumberDialog.title')}
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('workspace.header.campaignNumberDialog.description')}
          </p>
        </div>
      }
      onClose={closeDialog}
      testId="campaign-number-dialog"
    >
      <form id="campaign-number-form" onSubmit={handleSubmit(handleCampaignNumberSubmit)}>
        <div className="space-y-1.5">
          <Label htmlFor="campaign-number-input">
            {t('workspace.header.campaignNumberDialog.fieldLabel')}
          </Label>
          <Input
            id="campaign-number-input"
            aria-invalid={errors.campaignNumber ? true : undefined}
            error={Boolean(errors.campaignNumber)}
            {...register('campaignNumber', { validate: validateCampaignNumber })}
          />
          {errors.campaignNumber ? (
            <p className="text-xs text-destructive">{errors.campaignNumber.message}</p>
          ) : null}
          <div className="flex justify-end text-xs text-muted-foreground">
            <span>
              {campaignNumberValue.trim().length}/{CAMPAIGN_NUMBER_MAX_LENGTH}
            </span>
          </div>
        </div>
      </form>
    </ModalDialog>
  );
}
